import { Entry } from 'contentful'
import { z } from 'zod'
import { getContentfulClient } from './contentfulClient'

/**
 * Core shape that the Resurrection page needs. Keep fields minimal for MVP;
 * extend later without breaking changes.
 */
export interface ResurrectionContent {
  slug: string
  couponCode: string
  urlParams: string
  heroTitle: string
  heroSubtitle: string
  // Optional
  heroText?: string
  heroImage?: string
  heroImageMobile?: string
  menuSectionTitle?: string
  highlightedMealsIds?: number[]
  browserTitle?: string
  newDesign?: boolean
  conditionsCopy?: string
}

const CONTENT_TYPE_ID = 'resurrectionLanding'

// Helper: flatten Contentful rich-text nodes (or plain strings) to a simple text string
function extractText(node: any): string {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractText).join(' ')
  if (typeof node === 'object') {
    if ('value' in node && typeof node.value === 'string') return node.value
    if ('content' in node) return extractText(node.content)
  }
  return ''
}

// Zod schema for the fields returned by Contentful
const ResurrectionContentSchema = z
  .object({
    slug: z.string(),
    couponCode: z.string(),
    urlParams: z
      .string()
      .optional()
      .default(''),
    // titles might be missing; default to empty string
    heroTitle: z
      .string()
      .optional()
      .default(''),
    // accept rich-text or string
    heroSubtitle: z.union([z.string(), z.any()]).optional(),
    heroText: z.union([z.string(), z.any()]).optional(),
    heroImage: z.any().optional(), // asset object, we'll resolve to URL in transform
    heroImageMobile: z.any().optional(), // asset object, we'll resolve to URL in transform
    menuSectionTitle: z.string().optional(),
    highlightedMealsIds: z.array(z.number()).optional(),
    browserTitle: z.string().optional(),
    newDesign: z.boolean().optional(),
    // Contentful rich-text returns an object; accept string or any
    conditionsCopy: z.union([z.string(), z.any()]).optional()
  })
  .transform(data => ({
    slug: data.slug ?? '',
    couponCode: data.couponCode ?? '',
    urlParams: data.urlParams ?? '',
    heroTitle: data.heroTitle ?? '',
    heroSubtitle: extractText(data.heroSubtitle),
    heroText: extractText(data.heroText),
    heroImage: (data.heroImage as any)?.fields?.file?.url,
    heroImageMobile: (data.heroImageMobile as any)?.fields?.file?.url,
    menuSectionTitle: data.menuSectionTitle,
    highlightedMealsIds: data.highlightedMealsIds,
    browserTitle: data.browserTitle,
    newDesign: data.newDesign,
    conditionsCopy: extractText(data.conditionsCopy)
  }))

function mapEntry(entry: Entry<any>): ResurrectionContent {
  // Contentful JS SDK types are extremely generic; we validate with Zod instead of casting.
  return ResurrectionContentSchema.parse(entry.fields)
}

/**
 * Fetch a resurrection landing from Contentful by slug.
 * Throws if not found.
 */
export async function getResurrectionBySlug(
  slug: string
): Promise<ResurrectionContent> {
  const client = getContentfulClient()

  // Try as-is
  let res = await client.getEntries({
    content_type: CONTENT_TYPE_ID,
    'fields.slug': slug,
    include: 2,
    limit: 1
  })

  // If not found, try with/without trailing slash before query string
  if (!res.items[0]) {
    let withSlash = slug
    let withoutSlash = slug
    const queryIndex = slug.indexOf('?')

    if (queryIndex !== -1) {
      // Insert a slash before the query if not present
      if (slug[queryIndex - 1] !== '/') {
        withSlash = slug.slice(0, queryIndex) + '/' + slug.slice(queryIndex)
      }
      // Remove slash before the query if present
      if (slug[queryIndex - 1] === '/') {
        withoutSlash = slug.slice(0, queryIndex - 1) + slug.slice(queryIndex)
      }
    } else {
      // No query parameters - handle trailing slash at the end
      if (slug.endsWith('/')) {
        withoutSlash = slug.slice(0, -1)
      } else {
        withSlash = slug + '/'
      }
    }

    // Try with trailing slash
    if (withSlash !== slug) {
      res = await client.getEntries({
        content_type: CONTENT_TYPE_ID,
        'fields.slug': withSlash,
        include: 2,
        limit: 1
      })
    }

    // Try without trailing slash
    if (!res.items[0] && withoutSlash !== slug) {
      res = await client.getEntries({
        content_type: CONTENT_TYPE_ID,
        'fields.slug': withoutSlash,
        include: 2,
        limit: 1
      })
    }

    // For landing pages, try without resurrection- prefix
    if (!res.items[0] && slug.startsWith('resurrection-')) {
      const withoutResurrectionPrefix = slug.replace(/^resurrection-/, '')

      res = await client.getEntries({
        content_type: CONTENT_TYPE_ID,
        'fields.slug': withoutResurrectionPrefix,
        include: 2,
        limit: 1
      })
    }
  }

  const entry = res.items[0]
  if (!entry) {
    throw new Error(`Resurrection page not found for slug: ${slug}`)
  }

  return mapEntry(entry)
}
